import React, {Fragment, useCallback, useEffect, useState} from "react";
import {SideDrawerProperties} from "../SideDrawer";
import {useUserProvider} from "../../providers/UserProvider";
import ChatDialog from "./ChatDialog";
import {Box, createStyles, makeStyles, Typography} from "@material-ui/core";
import CustomMessageSideDrawer from "../CustomMessageSideDrawer";
import PreferenceChatDialog, { ChatPreferenceOptionsProps } from "./PreferenceChatDialog";
import { useIntl } from "react-intl";
import messages from "../notifications/messages"
import LoadingSpinner from "../LoadingSpinner";
import useLoadChats from "src/hooks/messages/useLoadChats";
import { useMessagesProvider } from "src/providers/MessagesProvider";
import UsersChat from "./UsersChat";
import { Chat, ChatUserData } from "myfitworld-model";

const useStyles = makeStyles(() =>
  createStyles({
    messageBox:{
      marginTop:'20px',
    },
    noMessagesText: {
      marginTop: 100,
      textAlign: 'center'
    }
  })
);

const MessagesDrawer = ({ isOpen, onClose }: Props) => {
  const { user } = useUserProvider();
  const {unreadUserCountMessages} = useMessagesProvider();
  const {formatMessage} = useIntl();
  const classes = useStyles();
  const {allChats, archivedChats, notArchivedChats, isLoading, setRefreshChats} = useLoadChats({userId: user?.id});

  const [isOpenFullChat, setOpenFullChat] = useState(false);
  const [isPreferenceChatDialog, setChatPreferenceDialog] = useState<{isOpen: boolean, selectedProp: ChatPreferenceOptionsProps | undefined}>({isOpen: false, selectedProp:undefined});
  const [dialogChatId, setDialogChatId] = useState<string>();
  const [dialogThreadUser, setDialogThreadUser] = useState<ChatUserData>();
  const [showArchived, setShowArchived] = useState<boolean>(false);

  const handleDialogOpen = (user: ChatUserData, chatId: string) => {
    setDialogThreadUser(user);
    setDialogChatId(chatId);
    setOpenFullChat(true);
  };

  function handleClosePreferenceChatDialog() {
    setChatPreferenceDialog({isOpen: false, selectedProp:undefined});
  }
  
  let options = [showArchived ? {label:formatMessage(messages.unarchive_chat), action: "UNARCHIVE_CHAT"}:{label: formatMessage(messages.archive_chat), action:"ARCHIVE_CHAT"}, {label:formatMessage(messages.delete_chat), action:"DELETE_CHAT"}];
  const handleDialogClose = useCallback((event: any) => {
    event.stopPropagation();
    setOpenFullChat(false);
  }, []);

  useEffect(() => {
    if (!user) return;
  }, [user]);

  const getUserId = (watchers: string[]) => {
    return watchers[0] === user?.id ? watchers[1] : watchers[0];
  }

  return (
    <Fragment>
      <CustomMessageSideDrawer 
        isOpen={isOpen} 
        onClose={onClose} 
        showArchived={showArchived} 
        setShowArchived={setShowArchived}
      >

        {isLoading && <LoadingSpinner/>}
        {!isLoading && ((showArchived && archivedChats.length === 0) || (!showArchived && notArchivedChats.length === 0)) &&
          <Typography variant='h3' className={classes.noMessagesText}>
            {showArchived && archivedChats.length === 0 && formatMessage(messages.no_archived_chats)}
            {!showArchived && notArchivedChats.length === 0 && formatMessage(messages.no_active_chats)}
          </Typography>
        }
        <Box className={classes.messageBox}>
          {!isLoading &&
            (showArchived ? archivedChats : notArchivedChats).map((chat:Chat, index) => {
              return (
                <UsersChat 
                  key={index} 
                  chatId={chat.id ?? ""}
                  userId={chat.watchers[0] === user?.id ? chat.watchers[1] : chat.watchers[0]} 
                  message={chat.lastMessageData} 
                  onClick={handleDialogOpen} 
                  setChatPreferenceDialog={setChatPreferenceDialog} 
                  options = {options}
                  user={chat.userFirstData.id === user?.id ? chat.userSecondData : chat.userFirstData}
                  unread={unreadUserCountMessages.get(getUserId(chat.watchers)) ?? 0}
                />
              )
            }) 
          }
        </Box>
      </CustomMessageSideDrawer>
      
      {/* Chat with messages dialog: */}
      {isOpenFullChat && dialogThreadUser?.id && dialogChatId && (
        <ChatDialog 
          unreadCount={unreadUserCountMessages.get(dialogThreadUser.id) ?? 0}
          isOpen={isOpenFullChat} 
          handleClose={handleDialogClose} 
          userId={dialogThreadUser.id} 
          userName={dialogThreadUser.name} 
          chatIds={[dialogChatId]}
          avatarURL={user?.avatarURL}
        />
      )}

      {/* Archive/Unarchive or delete 'Are you sure' Dialog */}
       {isPreferenceChatDialog.isOpen && isPreferenceChatDialog.selectedProp &&  (
        <PreferenceChatDialog 
          isOpen = {isPreferenceChatDialog.isOpen} 
          handleClose = {handleClosePreferenceChatDialog} 
          chatPreferenceOptions = {isPreferenceChatDialog.selectedProp}
          chat = {allChats.find(chat => chat.id === isPreferenceChatDialog.selectedProp?.selectedChatId)} 
          fetchAllChats = {() => setRefreshChats(true)}
        />
      )}
    </Fragment>
  );
};

export default MessagesDrawer;

interface Props extends SideDrawerProperties {}